import React, { useEffect, useState, useCallback, useRef } from 'react';
import { isMatch } from 'underscore';
import { pick } from 'ramda';
import { useDispatch } from 'react-redux';

import Form from 'js/components/common/Form';
import { I18n } from 'js/components/common/I18n';
import { Button } from '@interlayer/js/app/components/common/Button';
import { Input } from 'js/components/common/Fields/Input';
import ErrorMessage from 'js/components/common/Fields/ErrorMessage';
import { InputDate } from 'js/app/components/common/Fields/InputDate/InputDate';
import { BlockInfo } from '@interlayer/js/app/components/main/Registration/components/common/BlockInfo';
import {
    JumioStepLoading
} from '@interlayer/js/components/system/Page/buzz/components/JumioPopup/components/JumioPopupUploader/components';

import { clsx } from 'js/app/utils/clsx.util';
import { useTranslations } from 'js/hooks/i18n';
import { useUserSelector } from 'js/store/hooks';
import { RE_CHECK_PLAYER_TAGS, VALIDATION_FIELDS_JUMIO_POPUP } from './constants';
import { useFetchUserDetails } from 'js/hooks/user';
import { reverseDate } from 'js/app/utils/common.util';
import { setPlayerTags } from '@core/js/hooks/user/server';
import { useMountEffect } from '@core/js/hooks/useMountEffect';
import { setJumioStatus } from 'js/hooks/user/store/buzz/actions';
import { useLogout } from '@interlayer/js/hooks/auth/hooks/useLogout';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/app/utils/constants';
import { useDynamicTags } from '@interlayer/js/app/hooks/useDynamicTags';
import { changeDetails } from 'js/app/components/main/Registration/server';
import { getIsUserVerified } from 'js/app/hooks/user/hooks/useIsUserVerified';
import { useValidationRules } from '@interlayer/js/app/hooks/useValidationRules';

import style from './JumioPopupUpdateInformation.scss';

export const JumioPopupUpdateInformation = ({
    setIsHideClose,
    isAlreadyShowingLoading = false,
    isShowLogout,
    setIsShowLogout,
    closeForNative
}) => {
    const dispatch = useDispatch();
    const logout = useLogout();
    const isInitialDelayShown = useRef(isAlreadyShowingLoading);
    const [isDelay, setIsDelay] = useState(false);
    const [isInitialDelay, setIsInitialDelay] = useState(false);
    const t = useTranslations('playtech.mobile.system.id-verification.failed.');
    const tUserDetails = useTranslations('playtech.mobile.system.registration.label.');
    const [
        dTagIdvConfirmTag = 'exportable/idvconfirm',
        dTagBeforeIdvChangeDetailsDelay = '10000',
        dTagAfterIdvChangeDetailsDelay = '10000'
    ] = useDynamicTags([
        DYNAMIC_TAG_NAMES.idvConfirmTag,
        DYNAMIC_TAG_NAMES.beforeIdvChangeDetailsDelay,
        DYNAMIC_TAG_NAMES.afterIdvChangeDetailsDelay
    ]);
    const validationConfig = useValidationRules(VALIDATION_FIELDS_JUMIO_POPUP, 'JumioPopupUpdateInformation');

    const { userDetails, allPlayerTags: playerTags } = useUserSelector(pick(['userDetails', 'allPlayerTags']));
    const fetchUserDetails = useFetchUserDetails();

    const initialValues = {
        firstname: userDetails.firstName,
        lastname: userDetails.lastName,
        dateOfBirth: reverseDate(userDetails?.birthDate || '')
    };

    const handleInitialCheckAgeVerificationStatus = useCallback(() => {
        fetchUserDetails()
            .then((response) => {
                if (getIsUserVerified(response?.ageVerificationStatus)) {
                    dispatch(setJumioStatus('done'));
                }

                setIsInitialDelay(false);
            });
    }, [dispatch, fetchUserDetails]);

    const handleCheckAgeVerificationStatus = useCallback(() => {
        fetchUserDetails()
            .then((response) => {
                dispatch(setJumioStatus(getIsUserVerified(response?.ageVerificationStatus) ? 'done' : ''));

                setIsDelay(false);
            });
    }, [dispatch, fetchUserDetails]);

    const handlerSubmit = ({ firstname, lastname, dateOfBirth }) => {
        if (isMatch(initialValues, { firstname, lastname, dateOfBirth })) {
            dispatch(setJumioStatus(''));
            return;
        }

        return changeDetails({
            dataMap: {
                firstname,
                lastname,
                birthdate: reverseDate(dateOfBirth)
            }
        })
            .then(() => {
                setPlayerTags([{ name: dTagIdvConfirmTag }]);
                setIsDelay(true);
            })
            .catch(error => error.response?.data.errorMessage);
    };

    const handleExit = () => {
        logout({ isQuiet: true, afterLogout: closeForNative });
    };

    useMountEffect(() => {
        if (!isInitialDelayShown.current) {
            setIsInitialDelay(true);
            isInitialDelayShown.current = true;
        }
    }, []);

    useEffect(() => {
        if (isDelay && playerTags && RE_CHECK_PLAYER_TAGS.some((tag) => playerTags[tag])) {
            handleCheckAgeVerificationStatus();
        }
    }, [handleCheckAgeVerificationStatus, isDelay, playerTags]);

    useEffect(() => {
        if (isInitialDelay && userDetails?.ageVerificationStatus === 'failed') {
            setIsInitialDelay(false);
        }
    }, [isInitialDelay, userDetails?.ageVerificationStatus]);

    useEffect(() => {
        setIsHideClose(isDelay || isInitialDelay);
    }, [isDelay, isInitialDelay, setIsHideClose]);

    if (isDelay || isInitialDelay) {
        return (
            <JumioStepLoading
                isSimple
                translate={t}
                verificationTimeout={isInitialDelay
                    ? Number(dTagBeforeIdvChangeDetailsDelay)
                    : Number(dTagAfterIdvChangeDetailsDelay)
                }
                handlerAfterPassed={isInitialDelay
                    ? handleInitialCheckAgeVerificationStatus
                    : handleCheckAgeVerificationStatus
                }
            />
        );
    }

    if (isShowLogout) {
        return (
            <div className={style.exitWrapper}>
                <BlockInfo t={t} tKey="exitDescription" className={style.exitBlockInfo} />

                <Button
                    onClick={() => setIsShowLogout(false)}
                    className={clsx(style.btn, style.backButton)}
                >
                    {t('button.back')}
                </Button>

                <Button
                    view="link"
                    onClick={handleExit}
                    className={style.btn}
                >
                    {t('button.exit')}
                </Button>
            </div>
        );
    }

    return (
        <div>
            <BlockInfo t={t} tKey="description" className={style.blockInfo} />

            <Form
                onSubmit={handlerSubmit}
                validationConfig={validationConfig}
                initialValues={initialValues}
                render={({ hasValidationErrors, submitError, handleSubmit, submitting }) => (
                    <form onSubmit={handleSubmit}>
                        {submitError &&
                            <ErrorMessage
                                className={style.error}
                                error={submitError}
                                animate
                                isFormError
                            />
                        }

                        <Input
                            isShowValidationIcon
                            placeholder={tUserDetails('firstname')}
                            name="firstname"
                        />

                        <Input
                            isShowValidationIcon
                            placeholder={tUserDetails('lastname')}
                            name="lastname"
                        />

                        <InputDate
                            isShowValidationIcon
                            placeholder={tUserDetails('date')}
                            name="dateOfBirth"
                        />

                        <div className={style.formActions}>
                            <Button
                                type="submit"
                                className={style.btn}
                                disabled={submitting || hasValidationErrors}
                            >
                                {t('button.submit')}
                            </Button>
                        </div>
                    </form>
                )}
            />

            <I18n
                t={t}
                tKey="bottomDescription"
                className={style.bottomDescription}
            />
        </div>
    );
};
